import { useEffect, useState } from "react";
import { Droplets, Thermometer, Wind, SunMedium, Loader2, AlertTriangle, GlassWater, ChevronDown, ChevronUp } from "lucide-react";
import { useLang } from "@/context/LangContext";
import { fill } from "@/lib/utils";
import { fetchWeather, conditionOf, conditionIcon, CONDITION_LABEL, heatTier, peakToday } from "@/lib/weather";
import { HydrationReminder } from "@/components/HydrationReminder";

const REFRESH_MS = 10 * 60 * 1000;
const TIER_STYLE = {
  extreme: "bg-red-500/12 ring-red-500/40 text-red-700 dark:text-red-300",
  veryHot: "bg-orange-500/12 ring-orange-500/40 text-orange-700 dark:text-orange-300",
  hot: "bg-amber-500/12 ring-amber-500/40 text-amber-700 dark:text-amber-300",
};

// Current weather for the located position + hydration/heat-safety tips when it is hot.
export function HomeWeather({ pos, now, maghribAt }) {
  const { tc, lang } = useLang();
  const [wx, setWx] = useState(null);
  const [state, setState] = useState("loading"); // loading | ok | error
  const [open, setOpen] = useState(true);

  useEffect(() => {
    if (!pos) return;
    let cancelled = false;
    const load = async () => {
      const d = await fetchWeather(pos);
      if (cancelled) return;
      setWx(d); setState(d ? "ok" : "error");
    };
    setState("loading");
    load();
    const id = setInterval(load, REFRESH_MS);
    return () => { cancelled = true; clearInterval(id); };
  }, [pos?.[0], pos?.[1]]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!pos) return null;
  if (state === "loading" && !wx) {
    return <p className="inline-flex items-center gap-1.5 text-xs text-muted-foreground" data-testid="home-weather-loading"><Loader2 className="h-3.5 w-3.5 animate-spin" />{tc({ id: "Memuat cuaca…", en: "Loading weather…", ar: "جارٍ تحميل الطقس…" })}</p>;
  }
  if (!wx) {
    return <p className="text-xs text-muted-foreground" data-testid="home-weather-error">{tc({ id: "Cuaca tidak tersedia saat ini", en: "Weather unavailable right now", ar: "الطقس غير متاح حاليًا" })}</p>;
  }

  const c = wx.current || {};
  const cond = conditionOf(Number(c.weather_code));
  const Icon = conditionIcon(cond, c.is_day !== 0);
  const peak = peakToday(wx.hourly, c.time);
  // Tips consider the rest of today's peak too, so pilgrims can plan the day from the morning.
  const peakHotter = peak && peak.apparent > (c.apparent_temperature ?? -Infinity);
  const tier = heatTier(Math.max(c.apparent_temperature ?? -Infinity, peak?.apparent ?? -Infinity), Math.max(c.uv_index ?? 0, peak?.uv ?? 0));
  const n = (v, d = 0) => (v == null ? "—" : Number(v).toLocaleString(lang === "id" ? "id-ID" : undefined, { maximumFractionDigits: d }));

  return (
    <div className="space-y-3" data-testid="home-weather" data-condition={cond} data-tier={tier?.id || "comfortable"}>
      <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
        <div className="flex items-center gap-3">
          <span className="flex h-11 w-11 items-center justify-center rounded-2xl bg-[hsl(var(--gold))]/15 text-[hsl(var(--gold))]"><Icon className="h-6 w-6" /></span>
          <div>
            <p className="font-serif text-3xl leading-none tabular-nums" data-testid="home-weather-temp">{n(c.temperature_2m)}°<span className="text-base text-muted-foreground font-sans">C</span></p>
            <p className="text-xs text-muted-foreground" data-testid="home-weather-condition">{tc(CONDITION_LABEL[cond])}</p>
          </div>
        </div>
        <ul className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground tabular-nums">
          <li className="inline-flex items-center gap-1" data-testid="home-weather-feels"><Thermometer className="h-3.5 w-3.5" />{tc({ id: "Terasa", en: "Feels", ar: "الإحساس" })} {n(c.apparent_temperature)}°</li>
          <li className="inline-flex items-center gap-1" data-testid="home-weather-humidity"><Droplets className="h-3.5 w-3.5" />{n(c.relative_humidity_2m)}%</li>
          <li className="inline-flex items-center gap-1" data-testid="home-weather-uv"><SunMedium className="h-3.5 w-3.5" />UV {n(c.uv_index)}</li>
          <li className="inline-flex items-center gap-1" data-testid="home-weather-wind"><Wind className="h-3.5 w-3.5" />{n(c.wind_speed_10m)} km/h</li>
          {peak && (
            <li data-testid="home-weather-peak">
              {tc({ id: "Puncak panas", en: "Peak heat", ar: "ذروة الحر" })} {n(peak.apparent)}° · {peak.time.slice(11, 16)}
            </li>
          )}
        </ul>
      </div>

      {tier && (
        <div className={`rounded-2xl px-4 py-3 ring-1 ${TIER_STYLE[tier.id]}`} data-testid="home-heat-tips" data-tier={tier.id}>
          <button onClick={() => setOpen((o) => !o)} aria-expanded={open} data-testid="home-heat-toggle" className="flex w-full items-center gap-2 text-start">
            <AlertTriangle className="h-4 w-4 shrink-0" />
            <span className="flex-1 text-sm font-semibold" data-testid="home-heat-label" data-basis={peakHotter ? "peak" : "now"}>
              {tc(tier.label)}
              {peakHotter && ` · ${fill(tc({ id: "siang ini terasa {t}°", en: "feels {t}° later today", ar: "الإحساس {t}° لاحقًا اليوم" }), { t: n(peak.apparent) })}`}
              {" · "}{tc({ id: "Tips hidrasi & keselamatan", en: "Hydration & safety tips", ar: "نصائح الترطيب والسلامة" })}
            </span>
            {open ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
          </button>
          {open && (
            <ul className="mt-2 space-y-1.5 text-xs leading-relaxed text-foreground/90" data-testid="home-heat-list">
              {tier.tips.map((tip, i) => (
                <li key={tip.id} className="flex gap-2" data-testid={`home-heat-tip-${i}`}>
                  <GlassWater className="mt-0.5 h-3.5 w-3.5 shrink-0 opacity-70" />
                  <span>{tc(tip)}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      <HydrationReminder tier={tier} now={now} maghribAt={maghribAt} />
    </div>
  );
}
